import { getEnv } from "tools";
import { ApplicationRecord } from "models/ApplicationRecord";

/**
 * Events streamed by the pcs-ai-agents `POST /agent` endpoint.
 *
 * The server emits named SSE frames (`event: <type>\ndata: <json>\n\n`) where
 * the JSON payload is the event minus its `type`. We reconstruct the tagged
 * union below. `tool-*` events are surfaced for completeness but the v1 chat
 * UI only renders `text-delta` and reacts to `done`.
 */
export type AgentStreamEvent =
  | { type: "text-delta"; delta: string }
  | { type: "tool-call"; callId: string; name: string; args: unknown }
  | { type: "tool-result"; callId: string; name: string; result: unknown }
  | { type: "tool-error"; callId: string; name: string; message: string }
  | { type: "done"; finishReason: string };

/**
 * Thrown when the agent responds with a non-2xx status. `status` and the
 * optional hub-derived `reason` let the UI map the failure to user-facing
 * copy (e.g. 403 `persona_not_authorized`, 503 `persona_disabled`).
 */
export class AgentRequestError extends Error {
  constructor(readonly status: number, readonly reason?: string) {
    super(
      `Agent request failed with status ${status}${
        reason ? ` (${reason})` : ""
      }`
    );
    this.name = "AgentRequestError";
  }
}

export interface StreamAgentParams {
  /** The user's message for this turn. */
  message: string;
  /**
   * Client-generated conversation id, stable for the life of a chat session.
   * The server does not echo it back, so the client owns continuity.
   */
  conversationId: string;
  /** Aborts the in-flight request/stream (e.g. when the modal closes). */
  signal?: AbortSignal;
  /** Invoked for each decoded SSE event in arrival order. */
  onEvent: (event: AgentStreamEvent) => void;
}

const FRAME_DELIMITER = "\n\n";

/**
 * Parse a single SSE frame (the text between blank-line delimiters) into an
 * AgentStreamEvent, or null for frames we ignore (heartbeat comments, frames
 * with no `event:` line, or malformed JSON).
 */
export function parseSseFrame(frame: string): AgentStreamEvent | null {
  let eventName: string | undefined;
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    if (line === "" || line.startsWith(":")) {
      // Blank line or comment (the server's `: heartbeat <ts>` keep-alive).
      continue;
    }
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      // Per the SSE spec a single leading space after the colon is stripped.
      dataLines.push(line.slice("data:".length).replace(/^ /, ""));
    }
  }

  if (!eventName) {
    return null;
  }

  const payload = dataLines.join("\n");
  let data: Record<string, unknown> = {};
  if (payload.length > 0) {
    try {
      data = JSON.parse(payload) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  return { type: eventName, ...data } as AgentStreamEvent;
}

/**
 * POST a chat turn to pcs-ai-agents and stream the SSE response, invoking
 * `onEvent` for each frame as it arrives.
 *
 * Uses fetch + a ReadableStream reader rather than EventSource because the
 * request is a POST carrying an Authorization header, neither of which
 * EventSource supports. Throws AgentRequestError on a non-2xx response and
 * re-throws the fetch AbortError when `signal` fires (callers treat that as
 * an intentional cancel).
 */
export async function streamAgentResponse({
  message,
  conversationId,
  signal,
  onEvent,
}: StreamAgentParams): Promise<void> {
  const host = getEnv("NEXT_PUBLIC_PCS_AI_AGENTS_HOST");
  const response = await fetch(`${host}/agent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: ApplicationRecord.authHeader,
    },
    body: JSON.stringify({ message, conversationId }),
    signal,
  });

  if (!response.ok || !response.body) {
    let reason: string | undefined;
    try {
      const body = (await response.json()) as { reason?: string };
      reason = body?.reason;
    } catch {
      // Non-JSON error body — the status alone drives the UI message.
    }
    throw new AgentRequestError(response.status, reason);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    let result = await reader.read();
    while (!result.done) {
      buffer += decoder.decode(result.value, { stream: true });

      let delimiterIndex = buffer.indexOf(FRAME_DELIMITER);
      while (delimiterIndex !== -1) {
        const frame = buffer.slice(0, delimiterIndex);
        buffer = buffer.slice(delimiterIndex + FRAME_DELIMITER.length);
        const event = parseSseFrame(frame);
        if (event) {
          onEvent(event);
        }
        delimiterIndex = buffer.indexOf(FRAME_DELIMITER);
      }

      result = await reader.read();
    }
  } finally {
    reader.releaseLock();
  }
}
